import React, { useEffect, useState } from "react";

import { Button, Dropdown, Form, InputGroup, Modal, OverlayTrigger, Stack, Spinner, Tooltip } from "react-bootstrap";
import { ChevronDown, ChevronUp, Search, X } from "lucide-react";

import type { CoursesCatalogInterface, SectionsCatalogInterface } from "Stores/courseStore";
import type { unmappedTableState } from "./UnmappedSections";


interface CoursesModalProps {
    open: boolean;
    onClose: () => void;
    state: unmappedTableState;
    setState: (value: React.SetStateAction<unmappedTableState>) => void;
    courses: CoursesCatalogInterface[];
    sections: SectionsCatalogInterface[];
    onSubmitMap: () => Promise<void>;
}

export default function CoursesModal({ open, onClose, courses, state, setState, sections, onSubmitMap}: CoursesModalProps) {
    const [searchTerm, setSearchTerm] = useState<string>("");
	const [showDropdown, setShowDropdown] = useState<boolean>(false);

	const isCourseNotSelected = (): boolean => state.selectedCourse === null;

    const getSelectSectionsById = sections.filter((section) => state.sectionsSelection.includes(section.id));

    const filteredCourses = courses.filter((course) => course.course_title?.toLowerCase().includes(searchTerm.toLowerCase()) || course.subject_number?.toLowerCase().includes(searchTerm.toLowerCase()));

    // actions
    const handleModalClose = (): void => {
        onClose();
        setSearchTerm("");
        setState((prev) => ({ ...prev, openMap: false, selectedCourse: null }));
    };

    const onPressEnterKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.code === "Enter") {
            setShowDropdown(true);
        }
    };

    const onSelectCourse = (course: CoursesCatalogInterface) => {
		setState((prev) => ({ ...prev, selectedCourse: course }));

        if (searchTerm) {
            setSearchTerm("");
        }
	};

	const onRemoveSelectedCourse = () => setState((prev) => ({ ...prev, selectedCourse: null }));

    const removeSection = async (sectionId: number) => {
        const value = state.sectionsSelection.filter((e: number) => e !== sectionId);
        setState((prev) => ({ ...prev, sectionsSelection: value }));
	};

	const handleCourseSearch = (search: string) => {
		setSearchTerm(search);

		if (state.selectedCourse && search === "") {
			onRemoveSelectedCourse();
		}
	};

	const handleSubmitMapping = async () => {
		await onSubmitMap();
		setSearchTerm("");
	};

	useEffect(() => {
		if (searchTerm && !showDropdown) {
			setShowDropdown(true);
		}

	 }, [searchTerm]);

    return (
        <Modal show={open} onHide={onClose} centered className="courses-modal">
            <Modal.Body>
                <h1 className="courses-modal__header">Map section(s) to course</h1>

                <div className="courses-modal__content">
                    <Stack gap={3}>
                        <Stack gap={2} className="courses-modal__select-course">
                            <h2 className="mb-0">Select a course</h2>
                            <Dropdown show={showDropdown} onToggle={(isOpen) => setShowDropdown(isOpen)} className="courses-modal__dropdown">
                                <Dropdown.Toggle as={InputGroup} className="searchbox" tabIndex={0} onKeyDown={(e) => onPressEnterKey(e)}>
                                    <InputGroup.Text className="leading-icon">
                                        <Search className="search__bar-icon" aria-hidden="true" data-testid="icon-search" />
                                    </InputGroup.Text>
									<Form.Control
										type="text"
										placeholder={state.selectedCourse ? `${state.selectedCourse.subject_number + " - " + state.selectedCourse.course_title}` : "Select a course"}
										value={searchTerm}
										onChange={(e) => handleCourseSearch(e.target.value)}
									/>
                                    <InputGroup.Text className="trailing-icon">{showDropdown ? <ChevronUp size={20} /> : <ChevronDown size={20} />}</InputGroup.Text>
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    {filteredCourses.length > 0 ? (
                                        filteredCourses.map((course) => (
                                            <OverlayTrigger
                                                key={course.rid}
                                                placement="top"
                                                overlay={<Tooltip className="dashboard-card-tooltip courses-modal__option-tooltip">{`${course.subject_number} - ${course.course_title}`}</Tooltip>}
                                            >
                                                <Dropdown.Item className="option" onClick={() => onSelectCourse(course)}>
                                                    <span className="subject-number"> {course.subject_number} - </span> {course.course_title}
                                                </Dropdown.Item>
                                            </OverlayTrigger>
                                        ))
                                    ) : (
                                        <Dropdown.Item onClick={() => onRemoveSelectedCourse()}>
                                            <span className="no-result">No course(s) found.</span>
                                        </Dropdown.Item>
                                    )}
                                </Dropdown.Menu>
                            </Dropdown>
                        </Stack>

                        <Stack gap={2} className="courses-modal__selected-courses">
                            <h2 className="mb-0">Your selected sections</h2>
                            <div className="selected-courses">
                                {getSelectSectionsById.length === 0 && <span className="no-result text-center">No section(s) selected.</span>}

                                {getSelectSectionsById.map((section) => (
                                    <Stack key={section.id} direction="horizontal" className="selected">
                                        <span>{section.course_title}</span>
                                        <Button variant="ghost" onClick={() => removeSection(section.id)}>
                                            <X size={16} />
                                        </Button>
                                    </Stack>
                                ))}
                            </div>
                        </Stack>
                    </Stack>
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline" onClick={() => handleModalClose()}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={() => handleSubmitMapping()} disabled={isCourseNotSelected() || state.isMapping}>
					Confirm
					{state.isMapping && (
						<Spinner animation="border" size="sm" role="status" className="ms-2">
							<span className="visually-hidden">Loading...</span>
					  	</Spinner>
					)}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
